import { useCallback, useMemo } from 'react'

import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import find from 'lodash-es/find'
import findLast from 'lodash-es/findLast'
import uniq from 'lodash-es/uniq'

import { showSnackbar } from '@b-stock/fe-core/bstock-react'
import type {
  LocationModel,
  LocationsOutput,
  LocationsApiGetLocationsRequest,
} from '@b-stock/location-api-client'
import { LocationAttribute, LocationType } from '@b-stock/location-api-client'

import api from '@api/location'
import FormattedMessage from '@helpers/FormattedMessage'
import { sanitizeEmail } from '@helpers/sanitize'
import useUserInfo from '@helpers/useUserInfo'
import { usePerson } from '@queries/person'
import { queryClient } from '@store/data/queryClient'

import { useStatefulMutation } from '../mutation'

const baseKey = 'locations'

// This is a mess and we should try to get rid of it
type WonkyMaybeValidationResponseOrMaybeAddressFormThing = {
  primaryLine?: string
  address1?: string
  city?: string
  provinceCode?: string
  zipCode: string
  countryCode: string
  addressType?: string
}

type FindAddressArg = {
  address: WonkyMaybeValidationResponseOrMaybeAddressFormThing
  values?: {
    country: string
    state?: string
  }
}

const findAddress = async ({ address, values }: FindAddressArg) => {
  const data = await api.fetchAddresses({
    address: {
      addressLine1: address?.primaryLine || address?.address1,
      city: address?.city,
      provinceCode: address?.provinceCode || values?.state,
      zip: address?.zipCode,
      countryCode: address?.countryCode || values?.country?.toUpperCase(),
    },
  })

  return data?.locations
}

const getLatestAddress = async (
  address: WonkyMaybeValidationResponseOrMaybeAddressFormThing
) => {
  const data = await findAddress({ address })

  return data?.[0] ?? null
}

export const getAddressById = <T extends { id: string }>(
  id: string,
  addresses: T[]
): T | undefined => find(addresses, (address) => address.id === id)

type CreateLocationMutationArg = {
  useSuggested?: boolean
  validatedAddress: {
    attributes?: string[]
  }
  original: {
    attributes?: string[]
    address1: string
    address2?: string
  }
}

type UseLocationsFilters = Omit<
  LocationsApiGetLocationsRequest,
  'offset' | 'limit'
> & {
  offset?: number
  limit?: number
}

/**
 * Fetch locations data w/o mutations
 */
export const useLocationsReadOnly = (
  filters: UseLocationsFilters = {},
  opts?: Omit<
    UseQueryOptions<unknown, unknown, LocationsOutput | undefined, any>,
    'queryKey' | 'queryFn' | 'enabled' | 'placeholderData'
  >
) => {
  const { accountId } = useUserInfo()
  const { offset, limit, ...restFilters } = filters
  const queryKey = useMemo(
    () => [baseKey, accountId, filters] as const,
    [accountId, filters]
  )

  const query = useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      const resp = await api.fetchAddresses({
        address: {
          offset: offset ?? 0,
          limit: limit ?? 100,
          accountId: [accountId || ''],
          ...restFilters,
        },
      })

      return resp
    },
    refetchOnMount: 'always',
    ...opts,
    enabled: !!accountId,
    placeholderData: {
      responseType: 'FULL',
      total: 0,
      limit: 0,
      offset: 0,
      locations: [],
    },
  })

  const invalidateQuery = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey,
    })
  }, [queryKey])

  const { data } = query

  const defaultBillingAddress = useMemo<Partial<LocationModel>>(
    () =>
      find(data?.locations, ({ attributes }) =>
        attributes
          ? attributes.includes(LocationAttribute.DefaultBillable)
          : false
      ) || { countryCode: 'us' },
    [data?.locations]
  )
  const defaultShippingAddress = useMemo(
    () =>
      findLast(data?.locations, ({ attributes }) =>
        attributes?.includes(LocationAttribute.DefaultShippable)
      ) || null,
    [data?.locations]
  )
  const defaultDeliverableAddress = useMemo(
    () =>
      findLast(data?.locations, ({ attributes }) =>
        attributes?.includes(LocationAttribute.DefaultDeliverable)
      ) || {},
    [data?.locations]
  )

  return {
    ...query,
    invalidateQuery,
    isLoading: query.isLoading || query.isFetching,

    defaultBillingAddress,
    defaultShippingAddress,
    defaultDeliverableAddress,
  }
}

/**
 * Fetch locations data w/ mutations
 * @see useLocationsReadOnly for a lighter-weight alternative if you don't need the mutations
 */
export const useLocations = (
  filters: UseLocationsFilters = {},
  opts?: Omit<
    UseQueryOptions<unknown, unknown, LocationsOutput | undefined, any>,
    'queryKey' | 'queryFn' | 'enabled' | 'placeholderData'
  >
) => {
  const readOnlyData = useLocationsReadOnly(filters, opts)

  const { accountId } = useUserInfo()
  const { data: { phone = '', email = '', firstName, lastName } = {} } =
    usePerson()

  const { data, invalidateQuery } = readOnlyData

  const createMutation = useStatefulMutation(
    async (payload: CreateLocationMutationArg) => {
      const { useSuggested } = payload
      let defaultAddressAttr: string[] = []
      if (data && data.locations.length === 0) {
        defaultAddressAttr = [
          LocationAttribute.Billable,
          LocationAttribute.DefaultBillable,
        ]
      }
      const attributes =
        (payload?.validatedAddress?.attributes?.length &&
          payload?.validatedAddress?.attributes) ||
        (payload?.original?.attributes?.length &&
          payload?.original?.attributes) ||
        []
      const newPayload = {
        ...payload,
        original: {
          ...payload.original,
          addressLines: [
            payload.original.address1,
            ...[useSuggested ? [] : payload.original.address2],
          ],
          attributes: [...defaultAddressAttr, ...attributes],
        },
        validatedAddress: {
          ...payload.validatedAddress,
          attributes: [...defaultAddressAttr, ...attributes],
        },
      }

      const response = await api.createAddress({
        accountId,
        ...normalizeAddressData(newPayload),
      })

      return response
    },
    {
      mutationKey: ['useLocationsSaveAddressMutation'],
      onSuccess: invalidateQuery,
      onError: () => undefined,
    }
  )

  const deleteMutation = useStatefulMutation(
    async ({ id }: { id: string }) => api.deleteAddress({ id }),
    {
      mutationKey: ['useLocationsDeleteAddressMutation'],
      onSuccess: invalidateQuery,

      onError: async (error: any, payload: any) => {
        showSnackbar({
          content: (
            <FormattedMessage
              id="AddressBook.modal.delete.error"
              values={payload}
            />
          ),
          options: { type: 'error' },
        })
        return error
      },
    }
  )

  const editAddressMutation = useStatefulMutation(
    async (payload: any) => {
      const latestAddr =
        (await getLatestAddress(payload.data)) ||
        getAddressById(payload.id, data?.locations || [])

      if (latestAddr === undefined) {
        return
      }

      const { address1, address2, useSuggested, ...restOfPayload } =
        payload.data
      const phoneNumber = payload?.data?.contacts?.[0].phone || phone
      const contacts = [
        {
          name: payload?.data?.contacts?.[0].name
            ? payload?.data?.contacts?.[0].name
            : `${
                payload?.data?.differentFirstName ||
                payload?.data?.firstName ||
                firstName ||
                ''
              } ${
                payload?.data?.differentLastName ||
                payload?.data?.lastName ||
                lastName ||
                ''
              }`,
          phone: phoneNumber.replace(/\D+/g, ''),
          email: sanitizeEmail(payload?.data?.email || email),
        },
      ]

      const newPayload = {
        ...restOfPayload,
        ...(address1 && {
          addressLines: [address1, ...[useSuggested ? [] : address2]],
        }),
        contacts,
      }

      newPayload.provinceCode = newPayload.state
        ? newPayload.state
        : newPayload.provinceCode
      const edited = await api.updateAddress({
        id: latestAddr?.id,
        data: normalizeAddressData({ original: newPayload }),
      })
      return edited
    },
    {
      mutationKey: ['useLocationsEditAddressMutation'],
      onSuccess: invalidateQuery,
      onError: async (error: any) => {
        showSnackbar({
          content: <FormattedMessage id="Error.location.edit" />,
          options: { type: 'error' },
        })
        await invalidateQuery()
        return error
      },
      retry: 3,
    }
  )

  return {
    ...readOnlyData,

    createAddress: createMutation.mutateAsync,
    createAddressState: createMutation.state,

    deleteAddress: deleteMutation.mutateAsync,
    deleteAddressState: deleteMutation.state,

    editAddress: editAddressMutation.mutateAsync,
    editAddressState: editAddressMutation.state,

    getLatestAddress: getLatestAddress,
  }
}

const makeAttributesArr = (address: {
  attributes?: string[]
  addressType?: string
}): string[] => {
  let attributeArr = uniq([...(address?.attributes || [])])

  const addressTypeToLowerCase = address?.addressType?.toLowerCase() || ''

  if (addressTypeToLowerCase === 'residential') {
    attributeArr = [
      ...attributeArr,
      ...(attributeArr.includes(LocationAttribute.LiftgateRequired)
        ? []
        : [LocationAttribute.LiftgateRequired]),
    ]
  }

  return attributeArr
}

type NormalizeAddressDataInput = {
  original: any
  validatedAddress?: any
  useSuggested?: boolean
}
const normalizeAddressData = ({
  original = {},
  validatedAddress = {},
  useSuggested = false,
}: NormalizeAddressDataInput) => {
  const address = useSuggested ? validatedAddress : original
  const attributes = [
    ...(useSuggested ? ['NORMALIZED'] : []),
    ...makeAttributesArr(address),
  ]

  const addressLines = []
  if (useSuggested) {
    addressLines.push(address.primaryLine, address.secondaryLine)
  } else {
    addressLines.push(...address.addressLines)
  }

  const countryCode = address.countryCode
    ? address.countryCode?.toUpperCase()
    : address.country?.toUpperCase()

  const unknownAddressType =
    validatedAddress.addressType === 'UNKNOWN'
      ? LocationType.Residential
      : validatedAddress.addressType

  const type =
    original?.type ??
    (validatedAddress.addressType === 'COMMERCIAL'
      ? LocationType.Business
      : unknownAddressType)

  return {
    ...(addressLines.length && { addressLines }),
    ...(original?.addressName ? { addressName: original.addressName } : {}),
    city: address.city,
    ...((address.provinceCode || address.state) && {
      provinceCode: address.provinceCode ? address.provinceCode : address.state,
    }),
    zip: address.zipCode,
    countryCode,
    attributes,
    contacts: address.contacts,
    type,

    normalization: {
      dateNormalized: new Date(),
      original: {
        addressLines: [
          original.address1,
          ...(original.addressLine2 ? original.addressLine2 : []),
        ],
        city: original.city,
        provinceCode: original.state,
        zip: original.zipCode,
      },
      result: validatedAddress,
    },

    warehouse: {
      timezone: 'America/Los_Angeles', // TODO: figure out this hardcode
      avgFulfillmentDuration: 'P2D',
      hours: {
        pickup: {
          // 8AM-5PM monday - friday
          1: [{ from: '0800', to: '1700' }],
          2: [{ from: '0800', to: '1700' }],
          3: [{ from: '0800', to: '1700' }],
          4: [{ from: '0800', to: '1700' }],
          5: [{ from: '0800', to: '1700' }],
          6: null,
          7: null,
        },
        dropoff: {
          1: null,
          2: null,
          3: null,
          4: null,
          5: null,
          6: null,
          7: null,
          ...original?.warehouse?.hours?.dropoff,
        },
      },
      nonOperatingDates: [],
    },
  }
}

export const useLocationQuery = (locationId: string) =>
  useQuery({
    queryKey: ['fetchSingleLocation', locationId],

    queryFn: () => api.getAddress({ id: locationId }),
  })

export const useDocumentsQuery = (id: string) =>
  useQuery({
    queryKey: ['fetchSingleDocument', id],

    queryFn: () => (id ? api.fetchDocs({ id: [id] }) : null),
  })

export const useDefaultBillableLocationQuery = (
  opts?: Omit<
    UseQueryOptions<LocationModel | null, unknown, LocationModel | undefined>,
    'queryKey' | 'queryFn' | 'select'
  >
) => {
  const { accountId } = useUserInfo()
  if (!accountId) {
    throw new Error('Unable to extract a accountId from the store')
  }

  return useQuery({
    queryKey: ['defaultBillableLocation'],

    queryFn: async (): Promise<LocationModel | null> => {
      const resp = await api.fetchAddresses({
        address: {
          accountId: [accountId],
          attributes: [LocationAttribute.DefaultBillable],
        },
      })

      // TanStack Query doesn't allow undefined as query data, use null instead
      return resp?.locations?.[0] ?? null
    },

    select: (data: LocationModel | null): LocationModel | undefined =>
      data ?? undefined,

    ...opts,
  })
}

export const useDeliverableLocationQuery = (
  opts?: Omit<
    UseQueryOptions<LocationModel | null, unknown, LocationModel | undefined>,
    'queryKey' | 'queryFn' | 'select'
  >,
  requestProps?: { countryCode?: string; provinceCode?: string }
) =>
  useQuery({
    queryKey: ['deliverableLocation', requestProps],

    queryFn: async (): Promise<LocationModel | null> => {
      const resp = await api.fetchAddresses({
        address: {
          attributes: [LocationAttribute.Deliverable],
          ...(requestProps?.countryCode
            ? { countryCode: requestProps.countryCode }
            : {}),
          ...(requestProps?.provinceCode
            ? { provinceCode: requestProps.provinceCode }
            : {}),
        },
      })
      // TanStack Query doesn't allow undefined as query data, use null instead
      return resp?.locations?.[0] ?? null
    },

    select: (data: LocationModel | null): LocationModel | undefined =>
      data ?? undefined,

    ...opts,
  })

export const useDefaultDeliverableLocationQuery = (
  opts?: Omit<
    UseQueryOptions<LocationModel | null, unknown, LocationModel | undefined>,
    'queryKey' | 'queryFn' | 'select'
  >
) =>
  useQuery({
    queryKey: ['defaultDeliverableLocation'],

    queryFn: async (): Promise<LocationModel | null> => {
      const resp = await api.fetchAddresses({
        address: {
          attributes: [LocationAttribute.DefaultDeliverable],
        },
      })

      // TanStack Query doesn't allow undefined as query data, use null instead
      return resp?.locations?.[0] ?? null
    },

    select: (data: LocationModel | null): LocationModel | undefined =>
      data ?? undefined,

    ...opts,
  })
